// src/pages/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart3, TrendingUp, Clock, Star, Download, Eye, 
  BookOpen, Users, Calendar, Filter, Search,
  ChevronRight, Zap, Award, Bookmark, Target, CheckCircle,
  FileText, GraduationCap, CalendarDays, Activity, Loader, AlertCircle,
  MessageSquare, Send
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import MaterialCard from '../components/common/MaterialCard';
import Button from '../components/common/Button';
import { publicAPI } from '../services/api';

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('recent');
  const [recentMaterials, setRecentMaterials] = useState([]);
  const [popularMaterials, setPopularMaterials] = useState([]);
  const [recommendedMaterials, setRecommendedMaterials] = useState([]);
  const [bookmarkedMaterials, setBookmarkedMaterials] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboardStats, setDashboardStats] = useState(null);
  const navigate = useNavigate();

  // Fetch dashboard data
  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Fetch data when tab changes
  useEffect(() => {
    if (activeTab === 'recent') {
      fetchRecentMaterials();
    } else if (activeTab === 'popular') {
      fetchPopularMaterials();
    } else if (activeTab === 'recommended') {
      fetchRecommendedMaterials();
    }
  }, [activeTab]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch platform stats
      const statsResponse = await publicAPI.getPlatformStats();
      
      if (statsResponse.success) {
        setDashboardStats(statsResponse.data);
      }

      // Fetch recent materials (newest first)
      await fetchRecentMaterials();
      
      // Fetch popular materials (most downloaded)
      await fetchPopularMaterials();

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentMaterials = async () => {
    try {
      // Fetch materials sorted by creation date (newest first)
      const response = await publicAPI.getMaterials({ 
        limit: 12,
        sort: '-createdAt' // Most recent first
      });
      
      console.log('Recent materials response:', response);
      
      if (response.success && response.data) {
        // Extract materials from response
        const materials = response.data.materials || response.data;
        setRecentMaterials(Array.isArray(materials) ? materials : []);
        
        // Set first 3 as recommended
        setRecommendedMaterials(Array.isArray(materials) ? materials.slice(0, 3) : []);
      } else {
        setRecentMaterials([]);
        setRecommendedMaterials([]);
      }
    } catch (err) {
      console.error('Error fetching recent materials:', err);
      setRecentMaterials([]);
      setRecommendedMaterials([]);
    }
  };

  const fetchPopularMaterials = async () => {
    try {
      // Fetch materials sorted by downloads (most downloaded first)
      const response = await publicAPI.getMaterials({ 
        limit: 12,
        sort: '-downloads' // Most downloaded first
      });
      
      console.log('Popular materials response:', response);
      
      if (response.success && response.data) {
        // Extract materials from response
        const materials = response.data.materials || response.data;
        setPopularMaterials(Array.isArray(materials) ? materials : []);
      } else {
        setPopularMaterials([]);
      }
    } catch (err) {
      console.error('Error fetching popular materials:', err);
      setPopularMaterials([]);
    }
  };

  const fetchRecommendedMaterials = async () => {
    try {
      // Use existing recent materials or fetch new ones
      if (recentMaterials.length > 0) {
        setRecommendedMaterials(recentMaterials.slice(0, 3));
      } else {
        const response = await publicAPI.getMaterials({ limit: 3, sort: '-createdAt' });
        
        if (response.success && response.data) {
          const materials = response.data.materials || response.data;
          setRecommendedMaterials(Array.isArray(materials) ? materials : []);
        } else {
          setRecommendedMaterials([]);
        }
      }
    } catch (err) {
      console.error('Error fetching recommended materials:', err);
      setRecommendedMaterials([]);
    }
  };

  const handleMaterialClick = (material) => {
    navigate(`/material/${material._id || material.id}`);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  // Get materials for current tab - safely handle non-array values
  const getCurrentMaterials = () => {
    let materials;
    
    switch (activeTab) {
      case 'recent':
        materials = recentMaterials;
        break;
      case 'popular':
        materials = popularMaterials;
        break;
      case 'recommended':
        materials = recommendedMaterials;
        break;
      case 'bookmarked':
        materials = bookmarkedMaterials;
        break;
      default:
        materials = recentMaterials;
    }
    
    // Ensure it's always an array
    return Array.isArray(materials) ? materials : [];
  };

  const currentMaterials = getCurrentMaterials();

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Header />
      
      {error && (
        <div className="container mx-auto px-4 pt-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3 mb-6">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-sm text-red-800">{error}</p>
            <button 
              onClick={fetchDashboardData}
              className="ml-auto text-sm text-red-600 hover:text-red-800 font-medium"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-8">
        {/* Dashboard Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Learning Dashboard</h1>
                  <p className="text-gray-600">Welcome to Dire Dawa University Learning Materials Repository</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <form onSubmit={handleSearch} className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search materials..."
                  className="pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
                />
              </form>

            </div>
          </div>
          
          {/* Stats Grid - Skeletons or Active Data */}
          {(!dashboardStats || loading) ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 animate-pulse">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-gray-100 rounded-lg w-12 h-12"></div>
                    <div className="h-6 bg-gray-100 rounded w-16"></div>
                  </div>
                  <div className="h-8 bg-gray-200 rounded w-24 mb-2"></div>
                  <div className="h-4 bg-gray-100 rounded w-32"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all group"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-blue-50 rounded-lg group-hover:scale-110 transition-transform">
                    <BookOpen className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="text-sm text-green-600 font-medium bg-green-50 px-2 py-1 rounded">
                    Live Data
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  {dashboardStats.totalMaterials || 0}
                </div>
                <div className="text-gray-600 text-sm">Total Materials</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all group"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-green-50 rounded-lg group-hover:scale-110 transition-transform">
                    <Download className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="text-sm text-green-600 font-medium bg-green-50 px-2 py-1 rounded">
                    Live Data
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  {dashboardStats.totalDownloads || 0}
                </div>
                <div className="text-gray-600 text-sm">Total Downloads</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all group"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-purple-50 rounded-lg group-hover:scale-110 transition-transform">
                    <Users className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="text-sm text-green-600 font-medium bg-green-50 px-2 py-1 rounded">
                    Live Data
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  {dashboardStats.activeTeachers || 0}
                </div>
                <div className="text-gray-600 text-sm">Active Teachers</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all group"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-orange-50 rounded-lg group-hover:scale-110 transition-transform">
                    <GraduationCap className="w-6 h-6 text-orange-600" />
                  </div>
                  <div className="text-sm text-green-600 font-medium bg-green-50 px-2 py-1 rounded">
                    Live Data
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  {dashboardStats.activeSchools || 0}
                </div>
                <div className="text-gray-600 text-sm">Active Schools</div>
              </motion.div>
            </div>
          )}

          {/* Platform Stats - Skeletons or Active Data */}
          {(!dashboardStats || loading) ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 animate-pulse">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-gray-100 rounded-lg w-9 h-9"></div>
                    <div className="h-4 bg-gray-150 rounded w-24"></div>
                  </div>
                  <div className="h-7 bg-gray-200 rounded w-16"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Calendar className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="text-sm text-gray-600">Daily Downloads</div>
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {dashboardStats.dailyDownloads || 0}
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Users className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="text-sm text-gray-600">Total Students</div>
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {dashboardStats.totalStudents || 0}
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <BarChart3 className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="text-sm text-gray-600">Departments</div>
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {dashboardStats.totalDepartments || 0}
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <Activity className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="text-sm text-gray-600">Active Today</div>
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {dashboardStats.activeUsers || 0}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Materials */}
          <div className="lg:col-span-2">
            {/* Tabs */}
            <div className="bg-white rounded-xl shadow-lg mb-8 overflow-hidden">
              <div className="border-b border-gray-200 bg-gray-50">
                <div className="flex overflow-x-auto">
                  {[
                    { id: 'recent', label: 'Recently Added', icon: Clock },
                    { id: 'popular', label: 'Most Downloaded', icon: TrendingUp },
                    { id: 'recommended', label: 'Recommended', icon: Star },
                    { id: 'bookmarked', label: 'Bookmarked', icon: Bookmark },
                  ].map(tab => {
                    let count = 0;
                    if (tab.id === 'recent') count = recentMaterials.length;
                    else if (tab.id === 'popular') count = popularMaterials.length;
                    else if (tab.id === 'recommended') count = recommendedMaterials.length;
                    else if (tab.id === 'bookmarked') count = bookmarkedMaterials.length;
                    
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-6 py-4 font-medium whitespace-nowrap transition-colors relative ${
                          activeTab === tab.id
                            ? 'text-blue-600'
                            : 'text-gray-600 hover:text-blue-600'
                        }`}
                      >
                        <tab.icon className="w-5 h-5" />
                        {tab.label}
                        <span className="ml-2 px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-full">
                          {count}
                        </span>
                        {activeTab === tab.id && (
                          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Materials Grid */}
              <div className="p-6">
                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-pulse">
                    {[1, 2, 3, 4].map(i => (
                      <div key={i} className="bg-white p-6 rounded-xl border border-gray-100 shadow-md h-[180px] flex flex-col justify-between">
                        <div>
                          <div className="h-5 bg-gray-200 rounded w-3/4 mb-3"></div>
                          <div className="h-3 bg-gray-250 rounded w-1/2"></div>
                        </div>
                        <div className="space-y-2 pt-4 border-t border-gray-100">
                          <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                          <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : currentMaterials.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600">No materials available</p>
                    {activeTab === 'bookmarked' ? (
                      <p className="text-sm text-gray-500 mt-2">Login to see your bookmarked materials</p>
                    ) : activeTab === 'popular' ? (
                      <p className="text-sm text-gray-500 mt-2">No popular materials found. Check back when teachers upload more content.</p>
                    ) : activeTab === 'recent' ? (
                      <p className="text-sm text-gray-500 mt-2">No recent materials found. New uploads will appear here.</p>
                    ) : (
                      <p className="text-sm text-gray-500 mt-2">Materials will appear here as teachers upload them</p>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {currentMaterials.map((material, index) => (
                      <motion.div
                        key={material._id || material.id || index}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.25 }}
                      >
                        <MaterialCard 
                          material={material} 
                          onClick={() => handleMaterialClick(material)}
                          type={activeTab === 'popular' ? 'popular' : activeTab === 'recent' ? 'recent' : 'default'}
                        />
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <Link to="/search" className="block w-full">
                  <button className="w-full flex items-center justify-between p-3 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors">
                    <span className="font-medium">Search Materials</span>
                    <Search className="w-5 h-5" />
                  </button>
                </Link>
                <Link to="/browse" className="block w-full">
                  <button className="w-full flex items-center justify-between p-3 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors">
                    <span className="font-medium">Browse Schools</span>
                    <GraduationCap className="w-5 h-5" />
                  </button>
                </Link>
                <Link to="/search" className="block w-full">
                  <button className="w-full flex items-center justify-between p-3 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors">
                    <span className="font-medium">All Materials</span>
                    <FileText className="w-5 h-5" />
                  </button>
                </Link>
                {localStorage.getItem('token') && (
                  <Link to="/teachers?tab=profile" className="block w-full">
                    <button className="w-full flex items-center justify-between p-3 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition-colors">
                      <span className="font-medium">My Profile</span>
                      <Users className="w-5 h-5" />
                    </button>
                  </Link>
                )}
              </div>
            </div>

            {/* Platform Overview */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Platform Overview</h3>
                <Award className="w-5 h-5 text-yellow-500" />
              </div>
              {dashboardStats ? (
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-700 font-medium">Materials Today</span>
                    <span className="text-gray-900 font-bold">{dashboardStats.materialsToday || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-700 font-medium">New Teachers</span>
                    <span className="text-gray-900 font-bold">{dashboardStats.newTeachers || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-700 font-medium">Active Users</span>
                    <span className="text-gray-900 font-bold">{dashboardStats.activeUsers || 0}</span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <Loader className="w-6 h-6 text-gray-400 animate-spin mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">Loading platform stats...</p>
                </div>
              )}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <Link to="/browse" className="w-full text-center text-blue-600 hover:text-blue-800 font-medium block">
                  Explore Schools & Departments
                </Link>
              </div>
            </div>

            {/* Feedback & Support */}
            <div className="bg-gradient-to-br from-indigo-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
              <div className="flex items-center gap-3 mb-4">
                <MessageSquare className="w-6 h-6" />
                <h3 className="text-lg font-bold">Feedback</h3>
              </div>
              <p className="text-blue-100 text-sm mb-6">
                Have a suggestion or found a bug? Let us know so we can improve.
              </p>
              <Link to="/feedback" className="block w-full">
                <button className="w-full py-3 bg-white text-blue-600 font-bold rounded-lg hover:bg-blue-50 transition-all flex items-center justify-center gap-2">
                  <Send className="w-4 h-4" />
                  Send Feedback
                </button>
              </Link>
            </div>

            {/* Quick Links */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <Calendar className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-bold text-gray-900">Quick Links</h3>
              </div>
              <div className="space-y-3">
                <Link to="/feedback" className="block p-2 hover:bg-blue-50 rounded-lg transition-colors">
                  <div className="font-medium text-gray-900">Contact & Support</div>
                  <div className="text-sm text-gray-600">Get help with using the platform</div>
                </Link>
                <Link to="/search" className="block p-2 hover:bg-blue-50 rounded-lg transition-colors">
                  <div className="font-medium text-gray-900">All Materials</div>
                  <div className="text-sm text-gray-600">Search the entire repository</div>
                </Link>
              </div>
            </div>

            {/* Platform Features */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl shadow-lg p-6 text-white">
              <div className="flex items-center gap-3 mb-4">
                <Zap className="w-6 h-6" />
                <h3 className="text-lg font-bold">Platform Features</h3>
              </div>
              <div className="space-y-3">
                <div className="p-3 bg-white/10 rounded-lg backdrop-blur-sm">
                  <div className="text-sm font-medium mb-1">Organized Repository</div>
                  <div className="text-xs text-green-200">Materials categorized by school & department</div>
                </div>
                <div className="p-3 bg-white/10 rounded-lg backdrop-blur-sm">
                  <div className="text-sm font-medium mb-1">Teacher Verified</div>
                  <div className="text-xs text-green-200">All materials verified by authorized teachers</div>
                </div>
                <div className="p-3 bg-white/10 rounded-lg backdrop-blur-sm">
                  <div className="text-sm font-medium mb-1">Easy Access</div>
                  <div className="text-xs text-green-200">No registration required for students</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Dashboard;